import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Paperclip, Trash2, Upload } from "lucide-react";
import { api, errMsg, INCOTERMS, UNITS, specValue, uploadFile } from "@/lib/api";
import { useSpecSchema } from "@/context/SpecSchemaContext";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Field, Input, Select, Textarea, labelCls } from "@/components/Ui";
import { MandatoryTag } from "@/components/StatusChip";

const CERTS = ["IECEx", "ATEX", "UL", "CSA", "Material test certificate (EN 10204 3.1)",
  "Certificate of origin", "Factory acceptance test"];
const KINDS = [["drawing", "Drawing"], ["boq", "BOQ"], ["datasheet", "Datasheet"],
  ["specification", "Specification"], ["other", "Other"]];

const initial = {
  title: "", category: "", subcategory: "", requested_brand: "", brand_requirement: "equivalent",
  quantity: "", unit: "PCS", required_delivery_date: "", closing_date: "", delivery_location: "",
  incoterm: "", notes: "", company_name: "", contact_name: "", email: "", phone: "",
};

const today = () => new Date().toISOString().slice(0, 10);

export default function Rfq() {
  const [params] = useSearchParams();
  const multiIds = (params.get("products") || "").split(",").filter(Boolean);
  const productId = params.get("product") || multiIds[0] || null;
  const { user } = useAuth();
  const schema = useSpecSchema();

  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [alternates, setAlternates] = useState([]);
  const [form, setForm] = useState(initial);
  const [spec, setSpec] = useState({});
  const specInputs = schema.inputsFor(form.category);
  const specDisplay = schema.displayFor(form.category);


  const [certs, setCerts] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [kind, setKind] = useState("specification");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [options, setOptions] = useState({});
  const fileRef = useRef(null);
  const isBuyer = user?.role === "buyer";

  useEffect(() => {
    api.post("/events", { name: "rfq_started" }).catch(() => {});
    api.get("/filters").then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!productId) return;
    api.get(`/products/${productId}`).then((r) => {
      setProduct(r.data);
      setSpec(r.data.spec || {});
      setForm((f) => ({
        ...f,
        title: f.title || `${r.data.part_number} — ${r.data.name}`,
        category: r.data.category || "",
        subcategory: r.data.subcategory || "",
        requested_brand: r.data.brand || r.data.manufacturer_name || "",
      }));
    }).catch(() => setProduct(null));
  }, [productId]);

  useEffect(() => {
    const alts = multiIds.filter((i) => i !== productId);
    if (!alts.length) return setAlternates([]);
    Promise.all(alts.map((i) => api.get(`/products/${i}`).then((r) => r.data).catch(() => null)))
      .then((rows) => setAlternates(rows.filter(Boolean)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("products"), productId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Keep the closing date inside the delivery window if the delivery date moves earlier.
  const setDeliveryDate = (e) => {
    const required_delivery_date = e.target.value;
    setForm((f) => ({
      ...f,
      required_delivery_date,
      closing_date: required_delivery_date && f.closing_date > required_delivery_date
        ? required_delivery_date : f.closing_date,
    }));
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const data = await uploadFile(file, kind);
        setAttachments((a) => [...a, data]);
      } catch (err) {
        toast.error(`${file.name}: ${errMsg(err)}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.closing_date && form.required_delivery_date
        && form.closing_date > form.required_delivery_date) {
      return toast.error("The RFQ closing date must be on or before the required delivery date");
    }
    setBusy(true);
    const payload = {
      title: form.title,
      product_id: productId || null,
      alternate_product_ids: alternates.map((a) => a.id),
      category: form.category || null,
      subcategory: form.subcategory || null,
      requested_brand: form.requested_brand || null,
      brand_requirement: form.brand_requirement,
      spec: Object.fromEntries(Object.entries(spec).filter(([, v]) => v !== "" && v != null)),
      quantity: Number(form.quantity),
      unit: form.unit,
      required_delivery_date: form.required_delivery_date || null,
      closing_date: form.closing_date || null,
      delivery_location: form.delivery_location,
      incoterm: form.incoterm || null,
      required_certificates: certs,
      notes: form.notes || null,
      attachments: attachments.map((a) => ({ file_id: a.file_id, filename: a.filename,
        size: a.size, content_type: a.content_type, kind: a.kind || "other" })),
    };
    try {
      if (isBuyer) {
        const { data } = await api.post("/buyer/rfqs", payload);
        setDone(data);
      } else {
        const { data } = await api.post("/rfqs/public", {
          ...payload, company_name: form.company_name, contact_name: form.contact_name,
          email: form.email, phone: form.phone || null,
        });
        setDone(data);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="mx-auto max-w-2xl px-5 py-20 text-center" data-testid="rfq-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-[#13795B]" />
        <h1 className="mt-5 font-display text-3xl font-black tracking-tight text-[#102A43]">
          RFQ submitted
        </h1>
        <p className="mt-3 text-sm text-[#667788] md:text-base">
          Your reference is&nbsp;
          <span data-testid="rfq-reference" className="font-display font-black text-[#102A43]">
            {done.rfq_number}
          </span>
          . {done.matched_suppliers > 0
            ? `${done.matched_suppliers} matching supplier${done.matched_suppliers === 1 ? "" : "s"} have been notified.`
            : "No supplier currently matches this specification — the ADIT team will source it manually."}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {isBuyer ? (
            <Btn data-testid="rfq-view-mine" onClick={() => navigate("/buyer/rfqs")}>Open my RFQs</Btn>
          ) : (
            <Link to="/register?role=buyer"><Btn data-testid="rfq-create-account">Create a buyer account to track quotes</Btn></Link>
          )}
          <Link to="/search"><Btn variant="ghost" data-testid="rfq-back-to-search">Continue searching</Btn></Link>
        </div>
      </div>
    );

  return (
    <div className="mx-auto max-w-[1240px] px-5 py-10" data-testid="rfq-page">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#147D78]">
        Request for quotation
      </p>
      <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-[#102A43] sm:text-4xl">
        Submit an RFQ
      </h1>
      <p className="mt-3 max-w-2xl text-sm text-[#667788] md:text-base">
        {product
          ? "The selected product and its normalized specification are attached automatically. Adjust any required value before submitting."
          : "Submit a blank enquiry, or start from a product page to prefill the technical specification."}
      </p>
      <p className="mt-2 max-w-2xl text-sm font-semibold text-[#13795B]" data-testid="rfq-free-note">
        Raising an RFQ is free. No payment details are required and you are never obliged to award.
      </p>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <form onSubmit={submit}>
          <Card>
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">Requirement</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="RFQ title *" className="sm:col-span-2">
                <Input data-testid="rfq-title" required value={form.title} onChange={set("title")}
                  placeholder="e.g. Ex d cable glands M20 for substation package" />
              </Field>
              <Field label="Category">
                <Select data-testid="rfq-category" value={form.category} onChange={set("category")}
                  options={["", ...new Set([...(options.categories || []), ...schema.published])]} />
              </Field>
              <Field label="Subcategory">
                <Select data-testid="rfq-subcategory" value={form.subcategory} onChange={set("subcategory")}
                  options={["", ...(options.subcategories || [])]} />
              </Field>
              <Field label="Requested brand">
                <Select data-testid="rfq-brand" value={form.requested_brand} onChange={set("requested_brand")}
                  options={["", ...(options.brands || [])]} />
              </Field>
              <Field label="Brand requirement *">
                <Select data-testid="rfq-brand-requirement" value={form.brand_requirement}
                  onChange={set("brand_requirement")}
                  options={[["equivalent", "Equivalent accepted"], ["exact", "Exact brand required"]]} />
              </Field>
              <Field label="Quantity *">
                <Input data-testid="rfq-quantity" required type="number" min="1" value={form.quantity} onChange={set("quantity")} />
              </Field>
              <Field label="Unit">
                <Select data-testid="rfq-unit" value={form.unit} onChange={set("unit")} options={UNITS} />
              </Field>
              <Field label="Required delivery date">
                <Input data-testid="rfq-delivery-date" type="date" min={today()}
                  value={form.required_delivery_date} onChange={setDeliveryDate} />
              </Field>
              <Field label="RFQ closing date" hint="Last date suppliers can quote">
                <Input data-testid="rfq-closing-date" type="date" min={today()}
                  max={form.required_delivery_date || undefined}
                  value={form.closing_date} onChange={set("closing_date")} />
              </Field>
              <Field label="Delivery location *">
                <Input data-testid="rfq-delivery-location" required value={form.delivery_location}
                  onChange={set("delivery_location")} placeholder="City, Country — e.g. Abu Dhabi, UAE" />
              </Field>
              <Field label="Incoterm">
                <Select data-testid="rfq-incoterm" value={form.incoterm} onChange={set("incoterm")} options={INCOTERMS} />
              </Field>
            </div>
          </Card>

          <Card className="mt-5">
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
              Technical specification
            </h2>
            <p className="mt-1 text-sm text-[#667788]">
              Suppliers quote against these values. Leave a field blank if it is not a requirement.
            </p>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {specInputs.map(([k, l, type, options]) => (
                <Field key={k} label={l}>
                  {options && options.length ? (
                    <Select data-testid={`rfq-spec-${k}`} value={spec[k] ?? ""}
                      options={[["", "Any"], ...options.map((o) => [o, o])]}
                      onChange={(e) => setSpec({ ...spec, [k]: e.target.value })} />
                  ) : (
                    <Input data-testid={`rfq-spec-${k}`} type={type} step="0.1"
                      value={spec[k] ?? ""}
                      onChange={(e) => setSpec({ ...spec, [k]: type === "number" && e.target.value !== ""
                        ? Number(e.target.value) : e.target.value })} />
                  )}
                </Field>
              ))}
            </div>
          </Card>

          <Card className="mt-5">
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
              Required certificates
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {CERTS.map((c) => {
                const on = certs.includes(c);
                return (
                  <button key={c} type="button" data-testid={`rfq-cert-${c.slice(0, 5).toLowerCase().trim()}`}
                    onClick={() => setCerts(on ? certs.filter((x) => x !== c) : [...certs, c])}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                      on ? "border-[#102A43] bg-[#102A43] text-white" : "border-[#DFE6ED] bg-white text-[#334e68]"
                    }`}>
                    {c}
                  </button>
                );
              })}
            </div>
          </Card>

          <Card className="mt-5">
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
              Attachments &amp; notes
            </h2>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <Field label="Attachment type">
                <Select data-testid="rfq-attachment-kind" value={kind} onChange={(e) => setKind(e.target.value)} options={KINDS} />
              </Field>
              <input ref={fileRef} data-testid="rfq-file-input" type="file" multiple onChange={onFiles} className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt,.dwg,.dxf,.zip" />
              <Btn type="button" variant="ghost" data-testid="rfq-upload-btn" loading={uploading}
                onClick={() => fileRef.current?.click()}
                className="border-dashed border-[#147D78] bg-[#f2f8f7] text-[#147D78]">
                <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Attach drawings, BOQ, datasheets"}
              </Btn>
            </div>
            <p className="mt-2 text-xs text-[#8598ac]">PDF, images, Office, DWG/DXF or ZIP. Max 15 MB per file.</p>
            {attachments.length > 0 && (
              <ul className="mt-3 space-y-2" data-testid="rfq-attachment-list">
                {attachments.map((a) => (
                  <li key={a.file_id} className="flex items-center justify-between rounded-[9px] border border-[#DFE6ED] px-3 py-2 text-sm">
                    <span className="flex min-w-0 items-center gap-2">
                      <Paperclip className="h-3.5 w-3.5 shrink-0 text-[#667788]" />
                      <span className="truncate">{a.filename}</span>
                      <span className="shrink-0 rounded bg-[#eef1f5] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#667788]">
                        {a.kind}
                      </span>
                    </span>
                    <button type="button" data-testid={`remove-attachment-${a.file_id}`}
                      onClick={() => setAttachments((x) => x.filter((y) => y.file_id !== a.file_id))}
                      className="text-[#B42318]">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-5">
              <label className={labelCls}>Additional notes</label>
              <Textarea data-testid="rfq-additional" value={form.notes} onChange={set("notes")}
                placeholder="Packing instructions, project reference, certification handover requirements…" />
            </div>
          </Card>

          {!isBuyer && (
            <Card className="mt-5">
              <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">Your details</h2>
              <p className="mt-1 text-sm text-[#667788]">
                Have an account?{" "}
                <Link to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  className="font-semibold text-[#147D78] underline">
                  Sign in
                </Link>{" "}
                to track quotations against this RFQ.
              </p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <Field label="Company name *">
                  <Input data-testid="rfq-company" required value={form.company_name} onChange={set("company_name")} />
                </Field>
                <Field label="Contact name *">
                  <Input data-testid="rfq-contact" required value={form.contact_name} onChange={set("contact_name")} />
                </Field>
                <Field label="Email *">
                  <Input data-testid="rfq-email" required type="email" value={form.email} onChange={set("email")} />
                </Field>
                <Field label="Phone / WhatsApp">
                  <Input data-testid="rfq-phone" value={form.phone} onChange={set("phone")} placeholder="Include country code, e.g. +44 …" />
                </Field>
              </div>
            </Card>
          )}

          <Btn type="submit" variant="gold" data-testid="rfq-submit-btn" loading={busy}
            className="mt-6 w-full px-6 py-3.5 sm:w-auto">
            Submit RFQ
          </Btn>
        </form>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <Card data-testid="rfq-product-summary">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#147D78]">
              Specification attached
            </p>
            {product ? (
              <>
                <h3 className="mt-2 font-display text-lg font-black tracking-tight text-[#102A43]">
                  {product.part_number}
                </h3>
                <p className="text-sm text-[#667788]">{product.manufacturer_name} — {product.name}</p>
                <dl className="mt-4 space-y-2.5 border-t border-[#DFE6ED] pt-4">
                  {specDisplay.map(([key, l, field]) => (
                    <div key={key} className="flex justify-between gap-4 text-xs">
                      <dt className="flex items-center text-[#667788]">
                        {l}
                        <MandatoryTag mandatory={(schema.forCategory(form.category).mandatory || []).includes(key)} />
                      </dt>
                      <dd className="text-right font-semibold text-[#18232F]">{schema.specValue(spec, field)}</dd>
                    </div>
                  ))}
                </dl>
                {alternates.length > 0 && (
                  <div className="mt-4 border-t border-[#DFE6ED] pt-4" data-testid="rfq-alternates">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#147D78]">
                      Also acceptable · {alternates.length}
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {alternates.map((a) => (
                        <li key={a.id} className="text-xs text-[#334e68]" data-testid={`rfq-alternate-${a.id}`}>
                          <span className="font-semibold text-[#102A43]">{a.part_number}</span>{" "}
                          — {a.manufacturer_name}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-[11px] text-[#8598ac]">
                      Suppliers may quote any of these parts against this RFQ.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="mt-3 text-sm text-[#667788]">
                No product selected. Fill the technical specification manually, or{" "}
                <Link to="/search" className="font-semibold text-[#147D78] underline">pick a part from the catalogue</Link>{" "}
                so the specification prefills.
              </p>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
}
